import React, { Component } from 'react';
import ReactDOM from 'react-dom';

import InlineLicenseConfig from '~/components/legacy/InlineLicenseConfig.jsx';
import LicenseNavigatorV2 from '~/components/legacy/LicenseNavigatorV2.jsx';
import { buildProjectLicenseConfigFamilies } from '~/components/legacy/projectLicenseConfigFamilies.js';

const GROUPED_UPGRADE_LABELS = {
  solo_license: {
    usage_relation: 'Kooperation / Kundenprojekt',
    paid_budget_tier: 'Paid Media',
    format_branding_scope: 'Intro / Branding'
  },
  business_license: {
    usage_relation: 'Kooperation / Kundenprojekt',
    paid_budget_tier: 'Paid Media',
    format_branding_scope: 'Intro / Branding'
  },
  premium_license: {
    territory_scope: 'Mehrere Länder',
    rights_term: 'Laufzeitverlängerung'
  },
  campaign_license: {
    territory_scope: 'Mehrere Märkte',
    rights_term: 'Laufzeitverlängerung'
  }
};

const COMPATIBLE_OPTION_KEYS = {
  channel_scope_multi_channel: ['channel_scope_single_channel'],
  usage_relation_collab_on_own_channels: ['usage_relation_own_use'],
  marketing_intent_promotional: ['marketing_intent_non_promotional'],
  monetization_yes: ['monetization_no'],
  paid_budget_tier_250: ['paid_budget_tier_organic'],
  paid_budget_tier_2000: ['paid_budget_tier_organic', 'paid_budget_tier_250'],
  paid_budget_tier_10000: ['paid_budget_tier_organic', 'paid_budget_tier_250', 'paid_budget_tier_2000'],
  paid_budget_tier_50000: ['paid_budget_tier_organic', 'paid_budget_tier_250', 'paid_budget_tier_2000', 'paid_budget_tier_10000'],
  content_model_paid_product: ['content_model_standard'],
  format_branding_scope_series_intro_outro: ['format_branding_scope_none'],
  format_branding_scope_signature_branding: ['format_branding_scope_none', 'format_branding_scope_series_intro_outro'],
  usage_intensity_multi_use: ['usage_intensity_single_use'],
  territory_scope_one_country: ['territory_scope_local_broadcast'],
  territory_scope_dach: ['territory_scope_local_broadcast', 'territory_scope_one_country'],
  territory_scope_four_to_five_countries: ['territory_scope_local_broadcast', 'territory_scope_one_country', 'territory_scope_dach'],
  territory_scope_six_to_ten_countries: ['territory_scope_local_broadcast', 'territory_scope_one_country', 'territory_scope_dach', 'territory_scope_four_to_five_countries'],
  territory_scope_europe: ['territory_scope_local_broadcast', 'territory_scope_one_country', 'territory_scope_dach', 'territory_scope_four_to_five_countries', 'territory_scope_six_to_ten_countries'],
  territory_scope_global: ['territory_scope_local_broadcast', 'territory_scope_one_country', 'territory_scope_dach', 'territory_scope_four_to_five_countries', 'territory_scope_six_to_ten_countries', 'territory_scope_europe'],
  rights_term_6m: ['rights_term_3m'],
  rights_term_12m: ['rights_term_3m', 'rights_term_6m']
};

class LicenseNavigatorV2Flow extends Component {
  constructor(props) {
    super(props);

    this.handleNavigatorConfigure = this.handleNavigatorConfigure.bind(this);
    this.handleConfigBack = this.handleConfigBack.bind(this);
    this.handleConfigurationAdopt = this.handleConfigurationAdopt.bind(this);
    this.handleOpenSaveOverlay = this.handleOpenSaveOverlay.bind(this);
    this.handleCloseSaveOverlay = this.handleCloseSaveOverlay.bind(this);
    this.handlePresetNameChange = this.handlePresetNameChange.bind(this);
    this.handlePresetSave = this.handlePresetSave.bind(this);
    this.handleAuthSaveStart = this.handleAuthSaveStart.bind(this);
    this.handleResultEdit = this.handleResultEdit.bind(this);
    this.handleRestart = this.handleRestart.bind(this);
    this.handleNavigatorStateChange = this.handleNavigatorStateChange.bind(this);
    this.handleConfigStateChange = this.handleConfigStateChange.bind(this);

    this.state = {
      stage: 'navigator',
      selectedFamilyKey: null,
      recommendation: null,
      configuration: null,
      navigatorState: null,
      configState: null,
      presetName: '',
      showSaveOverlay: false,
      saveState: 'idle',
      savedPreset: null,
      publicConfiguration: null,
      configurationLinkState: 'idle'
    };
  }

  componentDidMount() {
    this.emitOverallProgressChange();
  }

  componentDidUpdate(prevProps, prevState) {
    const propsChanged =
      prevProps.showOverallProgress !== this.props.showOverallProgress ||
      prevProps.onOverallProgressChange !== this.props.onOverallProgressChange;
    const stateChanged =
      prevState.stage !== this.state.stage ||
      prevState.navigatorState !== this.state.navigatorState ||
      prevState.configState !== this.state.configState;

    if (propsChanged || stateChanged) {
      this.emitOverallProgressChange();
    }
  }

  families() {
    if (Array.isArray(this.props.families) && this.props.families.length > 0) {
      return this.props.families;
    }

    if (typeof window !== 'undefined' && Array.isArray(window.__projectLicenseConfigFamilies) && window.__projectLicenseConfigFamilies.length > 0) {
      return window.__projectLicenseConfigFamilies;
    }

    return buildProjectLicenseConfigFamilies(this.props.locale, this.props.scenarioCatalogByFamily);
  }

  i18nValue(key) {
    if (this.props.i18n && this.props.i18n[key] !== undefined) {
      return this.props.i18n[key];
    }

    return LicenseNavigatorV2Flow.defaultProps.i18n[key];
  }

  inlineConfigI18n() {
    return {
      ...LicenseNavigatorV2Flow.defaultProps.inlineConfigI18n,
      ...(this.props.inlineConfigI18n || {})
    };
  }

  selectedFamily() {
    return this.families().find((family) => family.key === this.state.selectedFamilyKey) || null;
  }

  normalizeScenario(scenario) {
    if (typeof scenario === 'string') {
      return { title: scenario };
    }

    return scenario || {};
  }

  optionQuestionMap(family) {
    return family.questions.reduce((map, question) => {
      question.options.forEach((option) => {
        map[option.key] = question.key;
      });

      return map;
    }, {});
  }

  selectedOrCompatibleOptionKeys(selectedOptionKeys) {
    return [...new Set((selectedOptionKeys || []).flatMap((key) => [key, ...(COMPATIBLE_OPTION_KEYS[key] || [])]))];
  }

  coveredScenarioStatus(family, configuration, rawScenario) {
    const scenario = this.normalizeScenario(rawScenario);
    const answers = configuration.answers || {};
    const selectedOptionKeys = configuration.selectedOptionKeys || [];
    const compatibleOptionKeys = this.selectedOrCompatibleOptionKeys(selectedOptionKeys);
    const optionQuestionMap = this.optionQuestionMap(family);
    const matchAll = scenario.match_all || [];
    const matchAny = scenario.match_any || [];
    const excludeAny = scenario.exclude_any || [];

    if (scenario.review) {
      return 'review';
    }

    if (excludeAny.some((key) => selectedOptionKeys.includes(key))) {
      return 'not_covered';
    }

    if (matchAll.length === 0 && matchAny.length === 0) {
      return 'covered';
    }

    const allMatched = matchAll.every((key) => compatibleOptionKeys.includes(key));
    const anyMatched = matchAny.length > 0 && matchAny.some((key) => compatibleOptionKeys.includes(key));

    const requiredQuestionsAnsweredWithDifferentOption = matchAll.some((key) => {
      const questionKey = optionQuestionMap[key];

      return questionKey && answers[questionKey] && !compatibleOptionKeys.includes(key);
    });

    const anyQuestions = [...new Set(matchAny.map((key) => optionQuestionMap[key]).filter((key) => key))];
    const allAnyQuestionsAnswered = anyQuestions.length > 0 && anyQuestions.every((questionKey) => answers[questionKey]);

    if (requiredQuestionsAnsweredWithDifferentOption) {
      return 'not_covered';
    }

    if (allAnyQuestionsAnswered && !anyMatched) {
      return 'not_covered';
    }

    if (allMatched && (matchAny.length === 0 || anyMatched)) {
      return 'covered';
    }

    return 'open';
  }

  coveredScenarios(family, configuration) {
    return (family.covered_scenarios || [])
      .map((scenario) => this.normalizeScenario(scenario))
      .filter((scenario) => this.coveredScenarioStatus(family, configuration, scenario) === 'covered');
  }

  selectedOption(question, configuration) {
    if (!question || !configuration) {
      return null;
    }

    return question.options.find((option) => option.key === configuration.answers[question.key]) || null;
  }

  handleNavigatorConfigure(recommendation) {
    this.setState({
      stage: 'config',
      selectedFamilyKey: recommendation.key,
      recommendation: recommendation,
      configuration: null,
      configState: null
    });
  }

  handleConfigBack() {
    this.setState({ stage: 'navigator' });
  }

  handleConfigurationAdopt(configuration) {
    this.setState({
      stage: 'result',
      selectedFamilyKey: configuration.familyKey,
      configuration: configuration,
      presetName: this.buildPresetName(configuration.familyTitle),
      showSaveOverlay: false,
      saveState: 'idle',
      savedPreset: null,
      publicConfiguration: null,
      configurationLinkState: 'idle'
    });
  }

  handleOpenSaveOverlay() {
    if (this.state.saveState === 'saved') {
      return;
    }

    this.setState({ showSaveOverlay: true });
  }

  handleCloseSaveOverlay() {
    this.setState({ showSaveOverlay: false });
  }

  handleResultEdit() {
    this.setState({ stage: 'config', showSaveOverlay: false, saveState: 'idle', savedPreset: null, publicConfiguration: null, configurationLinkState: 'idle' });
  }

  handleRestart() {
    this.setState({
      stage: 'navigator',
      selectedFamilyKey: null,
      recommendation: null,
      configuration: null,
      navigatorState: null,
      configState: null,
      presetName: '',
      showSaveOverlay: false,
      saveState: 'idle',
      savedPreset: null,
      publicConfiguration: null,
      configurationLinkState: 'idle'
    });
  }

  handleNavigatorStateChange(navigatorState) {
    this.setState({ navigatorState: navigatorState });
  }

  handleConfigStateChange(configState) {
    this.setState({ configState: configState });
  }

  maxConfigQuestionCount() {
    return this.families().reduce((maxCount, family) => Math.max(maxCount, family.questions?.length || 0), 0);
  }

  overallProgressState() {
    const maxNavigatorQuestionCount = this.state.navigatorState?.maxQuestionCount || 3;
    const totalSteps = maxNavigatorQuestionCount + this.maxConfigQuestionCount() + 1;

    let currentStep = 1;

    if (this.state.stage === 'config') {
      const configDisplayStep = this.state.configState?.displayStep || 1;
      currentStep = maxNavigatorQuestionCount + configDisplayStep;
    } else if (this.state.stage === 'result') {
      currentStep = totalSteps;
    } else {
      currentStep = this.state.navigatorState?.displayStep || 1;
    }

    const progressPercent = Math.round((currentStep / Math.max(totalSteps, 1)) * 100);

    return {
      currentStep: currentStep,
      totalSteps: totalSteps,
      progressPercent: progressPercent
    };
  }

  emitOverallProgressChange() {
    if (typeof this.props.onOverallProgressChange !== 'function') {
      return;
    }

    this.props.onOverallProgressChange(this.overallProgressState());
  }

  renderProgress() {
    const progressState = this.overallProgressState();

    return (
      <div className='license-navigator-v2-flow__progress'>
        <div className='license-navigator-v2-flow__progress-copy'>
          <span>{this.i18nValue('overall_progress')}</span>
          <span>{`${progressState.currentStep} / ${progressState.totalSteps}`}</span>
        </div>
        <div className='license-navigator-v2-flow__progress-track'>
          <div className='license-navigator-v2-flow__progress-fill' style={{ width: `${progressState.progressPercent}%` }}></div>
        </div>
      </div>
    );
  }

  handlePresetNameChange(event) {
    this.setState({
      presetName: event.target.value,
      saveState: 'idle'
    });
  }

  csrfToken() {
    if (typeof document === 'undefined') {
      return '';
    }

    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
  }

  currentReturnTo() {
    if (typeof window === 'undefined' || !window.location) {
      return '';
    }

    return `${window.location.pathname}${window.location.search || ''}`;
  }

  presetPayload() {
    const configuration = this.state.configuration || {};
    const family = this.selectedFamily();

    return {
      name: this.state.presetName.trim(),
      family_key: configuration.familyKey || family?.key || '',
      family_title: configuration.familyTitle || family?.title || '',
      estimated_price_cents: configuration.estimatedPriceCents || 0,
      selected_option_keys: Array(configuration.selectedOptionKeys || []),
      answers: configuration.answers || {}
    };
  }

  async submitPreset({ authMode = null } = {}) {
    const payload = this.presetPayload();

    if (!this.props.savePresetPath) {
      return {
        status: 'saved',
        preset: {
          name: payload.name,
          familyKey: payload.family_key,
          familyTitle: payload.family_title,
          estimatedPriceCents: payload.estimated_price_cents,
          selectedOptionKeys: payload.selected_option_keys,
          answers: payload.answers
        }
      };
    }

    const response = await fetch(this.props.savePresetPath, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken(),
        Accept: 'application/json'
      },
      body: JSON.stringify({
        preset: payload,
        auth_mode: authMode,
        return_to: this.currentReturnTo()
      })
    });

    if (!response.ok) {
      throw new Error(`Preset save failed with status ${response.status}`);
    }

    return response.json();
  }

  rememberSavedPreset(preset) {
    if (typeof window === 'undefined') {
      return;
    }

    const existingPresets = Array.isArray(window.__customerProjectLicensePresets) ? window.__customerProjectLicensePresets : [];
    window.__customerProjectLicensePresets = [preset, ...existingPresets.filter((item) => item.id !== preset.id)];
  }

  async handlePresetSave() {
    if (!this.state.presetName.trim()) {
      return;
    }

    this.setState({ saveState: 'saving' });

    try {
      const result = await this.submitPreset();
      const savedPreset = result.preset || null;

      if (savedPreset) {
        this.rememberSavedPreset(savedPreset);
      }

      this.setState({
        saveState: 'saved',
        showSaveOverlay: false,
        savedPreset: savedPreset
      });
    } catch (_error) {
      this.setState({ saveState: 'idle' });
    }
  }

  async handleAuthSaveStart(authMode) {
    this.setState({ saveState: 'saving' });

    try {
      const result = await this.submitPreset({ authMode: authMode });

      if (result.status === 'auth_required' && result.redirect_to && typeof window !== 'undefined') {
        this.setState({ saveState: 'redirecting' });
        window.location.assign(result.redirect_to);
        return;
      }

      this.setState({ saveState: 'idle' });
    } catch (_error) {
      this.setState({ saveState: 'idle' });
    }
  }

  async submitPublicConfiguration() {
    const configuration = this.state.configuration || {};

    if (!this.props.createConfigurationPath) {
      return { status: 'created', configuration: null };
    }

    const response = await fetch(this.props.createConfigurationPath, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken(),
        Accept: 'application/json'
      },
      body: JSON.stringify({
        configuration: {
          family_key: configuration.familyKey || '',
          selected_option_keys: Array(configuration.selectedOptionKeys || []),
          answers: configuration.answers || {}
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Configuration create failed with status ${response.status}`);
    }

    return response.json();
  }

  async ensurePublicConfiguration() {
    if (this.state.publicConfiguration) {
      return this.state.publicConfiguration;
    }

    this.setState({ configurationLinkState: 'creating' });

    try {
      const result = await this.submitPublicConfiguration();
      const publicConfiguration = result.configuration || null;

      this.setState({
        publicConfiguration: publicConfiguration,
        configurationLinkState: 'ready'
      });

      return publicConfiguration;
    } catch (_error) {
      this.setState({ configurationLinkState: 'idle' });
      return null;
    }
  }

  async copyTextToClipboard(value) {
    if (!value) {
      return;
    }

    if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(value);
      return;
    }

    if (typeof document === 'undefined') {
      return;
    }

    const input = document.createElement('input');
    input.value = value;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
  }

  async handleCopyConfigurationLink() {
    const publicConfiguration = await this.ensurePublicConfiguration();
    const detailUrl = publicConfiguration?.detailUrl || publicConfiguration?.detailPath;

    if (!detailUrl) {
      return;
    }

    await this.copyTextToClipboard(detailUrl);
    this.setState({ configurationLinkState: 'copied' });
  }

  async handleOpenConfigurationDetail() {
    const publicConfiguration = await this.ensurePublicConfiguration();
    const detailPath = publicConfiguration?.detailPath || publicConfiguration?.detailUrl;

    if (!detailPath || typeof window === 'undefined') {
      return;
    }

    window.location.assign(detailPath);
  }

  buildPresetName(familyTitle) {
    if (!familyTitle) {
      return '';
    }

    return this.props.locale === 'de' ? `Meine ${familyTitle}-Projektlizenz` : `My ${familyTitle} project license`;
  }

  renderConfiguredSummary(family, configuration) {
    return (
      <ul className='license-navigator-v2-flow__summary-list'>
        {family.questions.map((question) => {
          const option = this.selectedOption(question, configuration);

          if (!option) {
            return null;
          }

          return (
            <li className='license-navigator-v2-flow__summary-item' key={question.key}>
              <span className='license-navigator-v2-flow__summary-label'>{question.title}</span>
              <span className='license-navigator-v2-flow__summary-value'>{option.label}</span>
            </li>
          );
        })}
      </ul>
    );
  }

  recommendationTitle(familyTitle) {
    return this.i18nValue('ready_title').replace('%{family}', familyTitle || '');
  }

  resultBaseLabel(family) {
    return `${family.title}-Basis`;
  }

  resultUpgradeLabel(familyKey, question, selectedOption) {
    const groupedLabel = GROUPED_UPGRADE_LABELS?.[familyKey]?.[question.key];
    return groupedLabel || selectedOption.short_name || selectedOption.label;
  }

  resultUpgradeLabels(family, configuration) {
    const answers = configuration.answers || {};

    return family.questions.map((question) => {
      const selectedKey = answers[question.key];
      const selectedOption = question.options.find((option) => option.key === selectedKey);
      const defaultOption = question.options[0];

      if (!selectedOption || !defaultOption || selectedOption.key === defaultOption.key) {
        return null;
      }

      return this.resultUpgradeLabel(family.key, question, selectedOption);
    }).filter(Boolean).filter((label, index, labels) => labels.indexOf(label) === index);
  }

  renderResultHighlights(family, configuration) {
    const upgradeLabels = this.resultUpgradeLabels(family, configuration);

    return (
      <div className='license-navigator-v2-flow__result-highlights'>
        <span className='license-navigator-v2-flow__result-highlight-chip license-navigator-v2-flow__result-highlight-chip--base'>
          {this.resultBaseLabel(family)}
        </span>
        {upgradeLabels.map((label) => (
          <span className='license-navigator-v2-flow__result-highlight-chip license-navigator-v2-flow__result-highlight-chip--upgrade' key={label}>
            {label}
          </span>
        ))}
      </div>
    );
  }

  renderCoveredScenariosDetails(family, configuration) {
    const scenarios = this.coveredScenarios(family, configuration);

    if (scenarios.length === 0) {
      return null;
    }

    return (
      <details className='license-navigator-v2-flow__details license-navigator-v2-flow__details--scenarios'>
        <summary className='license-navigator-v2-flow__details-toggle'>{this.i18nValue('covered_scenarios')}</summary>
        <div className='license-navigator-v2-flow__details-body'>
          <p className='license-navigator-v2-flow__result-copy'>{this.i18nValue('covered_scenarios_copy')}</p>
          <div className='license-navigator-v2-flow__scenario-chips'>
            {scenarios.map((scenario) => (
              <div className='license-navigator-v2-flow__scenario-chip license-navigator-v2-flow__scenario-chip--covered' key={scenario.title}>
                {scenario.title}
              </div>
            ))}
          </div>
        </div>
      </details>
    );
  }

  renderAuthNotice() {
    return (
      <div className='license-navigator-v2-flow__auth-box'>
        <div className='license-navigator-v2-flow__result-section-title'>{this.i18nValue('save_requires_account')}</div>
        <p className='license-navigator-v2-flow__result-copy'>{this.i18nValue('save_requires_account_copy')}</p>
        <div className='license-navigator-v2-flow__actions'>
          <button
            type='button'
            className='license-navigator-v2-demo__cta'
            onClick={() => this.handleAuthSaveStart('sign_in')}
            disabled={this.state.saveState === 'saving' || this.state.saveState === 'redirecting'}
          >
            {this.i18nValue('sign_in_to_save')}
          </button>
          <button
            type='button'
            className='license-navigator-v2-demo__ghost'
            onClick={() => this.handleAuthSaveStart('sign_up')}
            disabled={this.state.saveState === 'saving' || this.state.saveState === 'redirecting'}
          >
            {this.i18nValue('create_account_to_save')}
          </button>
        </div>
      </div>
    );
  }

  renderSavedPresetState() {
    return (
      <div className='license-navigator-v2-flow__saved-box'>
        <div className='license-navigator-v2-flow__result-section-title'>{this.i18nValue('preset_saved')}</div>
        <p className='license-navigator-v2-flow__result-copy'>{this.i18nValue('preset_saved_copy')}</p>
        <div className='license-navigator-v2-flow__saved-name'>{this.state.savedPreset?.name || this.state.presetName}</div>
        <div className='license-navigator-v2-flow__actions'>
          <a className='license-navigator-v2-demo__cta' href={this.props.searchPath}>
            {this.i18nValue('search_with_preset')}
          </a>
          <button type='button' className='license-navigator-v2-demo__ghost' onClick={this.handleRestart}>
            {this.i18nValue('create_another_preset')}
          </button>
        </div>
      </div>
    );
  }

  renderConfigurationActions() {
    const actionsDisabled = this.state.configurationLinkState === 'creating';

    return (
      <div className='license-navigator-v2-flow__actions'>
        <button
          type='button'
          className='license-navigator-v2-demo__ghost'
          onClick={() => this.handleCopyConfigurationLink()}
          disabled={actionsDisabled}
        >
          {this.state.configurationLinkState === 'copied' ? this.i18nValue('copy_configuration_link_done') : (this.state.configurationLinkState === 'creating' ? this.i18nValue('creating_configuration_link') : this.i18nValue('copy_configuration_link'))}
        </button>
        <button
          type='button'
          className='license-navigator-v2-demo__ghost'
          onClick={() => this.handleOpenConfigurationDetail()}
          disabled={actionsDisabled}
        >
          {this.i18nValue('open_configuration_detail')}
        </button>
      </div>
    );
  }

  renderResultActions() {
    if (this.props.customerSignedIn) {
      return (
        <div className='license-navigator-v2-flow__actions license-navigator-v2-flow__actions--primary'>
          <button
            type='button'
            className='license-navigator-v2-demo__cta'
            onClick={this.handleOpenSaveOverlay}
            disabled={this.state.saveState === 'saved' || this.state.saveState === 'saving'}
          >
            {this.state.saveState === 'saved' ? this.i18nValue('save_preset_done') : this.i18nValue('save_preset')}
          </button>
          <button type='button' className='license-navigator-v2-demo__ghost' onClick={this.handleResultEdit}>
            {this.i18nValue('edit_configuration')}
          </button>
        </div>
      );
    }

    return this.renderAuthNotice();
  }

  renderSaveOverlay() {
    if (!this.props.customerSignedIn || !this.state.showSaveOverlay) {
      return null;
    }

    return (
      <div className='license-navigator-v2-flow__overlay' role='dialog' aria-modal='true'>
        <button
          type='button'
          className='license-navigator-v2-flow__overlay-backdrop'
          onClick={this.handleCloseSaveOverlay}
          aria-label={this.i18nValue('close_save_overlay')}
        ></button>
        <div className='license-navigator-v2-flow__overlay-card'>
          <div className='license-navigator-v2-flow__result-section-title'>{this.i18nValue('save_overlay_title')}</div>
          <p className='license-navigator-v2-flow__result-copy'>{this.i18nValue('save_overlay_copy')}</p>
          <label className='license-navigator-v2-flow__overlay-label' htmlFor='license-navigator-v2-preset-name'>
            {this.i18nValue('save_overlay_label')}
          </label>
          <input
            id='license-navigator-v2-preset-name'
            type='text'
            className='license-navigator-v2-flow__input'
            value={this.state.presetName}
            onChange={this.handlePresetNameChange}
            placeholder={this.i18nValue('save_overlay_placeholder')}
          />
          <div className='license-navigator-v2-flow__actions'>
            <button
              type='button'
              className='license-navigator-v2-demo__cta'
              onClick={this.handlePresetSave}
              disabled={!this.state.presetName.trim() || this.state.saveState === 'saving'}
            >
              {this.i18nValue('save_overlay_confirm')}
            </button>
            <button type='button' className='license-navigator-v2-demo__ghost' onClick={this.handleCloseSaveOverlay}>
              {this.i18nValue('save_overlay_cancel')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderAnswersDetails(family, configuration) {
    return (
      <details className='license-navigator-v2-flow__details'>
        <summary className='license-navigator-v2-flow__details-toggle'>{this.i18nValue('answers_toggle')}</summary>
        <div className='license-navigator-v2-flow__details-body'>
          <div className='license-navigator-v2-flow__result-section'>
            <div className='license-navigator-v2-flow__result-section-title'>{this.i18nValue('selected_answers')}</div>
            {this.renderConfiguredSummary(family, configuration)}
          </div>
        </div>
      </details>
    );
  }

  renderResult() {
    const family = this.selectedFamily();
    const configuration = this.state.configuration;

    if (!family || !configuration) {
      return null;
    }

    return (
      <div className='license-navigator-v2-flow__result-card'>
        <div className='license-navigator-v2-flow__result-label'>{this.i18nValue('ready_label')}</div>
        <h2 className='license-navigator-v2-flow__result-title'>{this.recommendationTitle(family.title)}</h2>
        <p className='license-navigator-v2-flow__result-copy'>{this.i18nValue('ready_copy')}</p>

        <div className='license-navigator-v2-flow__result-section'>
          <div className='license-navigator-v2-flow__result-section-title'>{this.i18nValue('estimated_price')}</div>
          <div className='license-navigator-v2-flow__result-price'>{this.formatPrice(configuration.estimatedPriceCents)}</div>
        </div>
        {this.renderResultHighlights(family, configuration)}

        {this.renderResultActions()}
        {this.renderConfigurationActions()}
        {this.renderCoveredScenariosDetails(family, configuration)}
        {this.renderAnswersDetails(family, configuration)}
        {this.state.saveState === 'saved' ? this.renderSavedPresetState() : null}
        {this.renderSaveOverlay()}
      </div>
    );
  }

  formatPrice(cents) {
    return new Intl.NumberFormat(this.props.locale === 'de' ? 'de-DE' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format((cents || 0) / 100);
  }

  renderConfig() {
    const family = this.selectedFamily();
    const smartNavigatorSkin = this.props.skin === 'smart_navigator_fullpage';

    if (!family) {
      return null;
    }

    return (
      <div className={smartNavigatorSkin ? 'license-navigator-v2-flow__config license-navigator-v2-flow__config--smart-navigator' : 'license-navigator-v2-flow__config'}>
        <div className='license-navigator-v2-flow__config-main'>
          <InlineLicenseConfig
            locale={this.props.locale}
            families={[family]}
            display_mode='wizard'
            panel_layout={smartNavigatorSkin ? 'questions_with_live_summary' : 'questions_first'}
            showIntro={false}
            showCustomizeLink={false}
            showResetLink={false}
            showProgressBar={false}
            showWizardProgress={false}
            showFlowHeading={false}
            showWizardNextButton={!smartNavigatorSkin}
            defaultExpandedFamilyKeys={[family.key]}
            forceExpandedFamilyKeys={[family.key]}
            familyHeaderEyebrow={this.i18nValue('recommended_license_family')}
            familyHeaderEyebrowMobileOnly={true}
            headerAction={{
              label: this.i18nValue('back_to_navigator'),
              onClick: this.handleConfigBack,
              className: 'inline-license-config__header-back-link'
            }}
            onWizardStateChange={this.handleConfigStateChange}
            onResultAction={this.handleConfigurationAdopt}
            resultActionLabel={this.i18nValue('finish_configuration')}
            skipWizardResultStep={true}
            i18n={this.inlineConfigI18n()}
          />
        </div>
        {smartNavigatorSkin ? null : (
          <aside className='license-navigator-v2-flow__config-intro'>
            <div className='license-navigator-v2-flow__config-intro-label'>{this.i18nValue('recommended_license_family')}</div>
            <div className='license-navigator-v2-flow__config-intro-title'>{this.state.recommendation?.title || family.title}</div>
            <p className='license-navigator-v2-flow__config-intro-copy'>{this.i18nValue('config_intro_copy')}</p>
          </aside>
        )}
      </div>
    );
  }

  renderNavigator() {
    return (
      <LicenseNavigatorV2
        locale={this.props.locale}
        showIntro={false}
        showSidebar={true}
        sidebarTitle={this.props.navigatorSidebarTitle}
        sidebarContextItems={this.props.navigatorSidebarContextItems}
        sidebarActionLabel={this.props.navigatorSidebarActionLabel}
        onSidebarAction={this.props.onNavigatorSidebarAction}
        showProgress={false}
        autoAdvanceOnComplete={true}
        onStateChange={this.handleNavigatorStateChange}
        onRecommendationConfigure={this.handleNavigatorConfigure}
        configureActionLabel={this.i18nValue('configure_recommendation')}
      />
    );
  }

  renderCurrentStage() {
    switch (this.state.stage) {
      case 'config':
        return this.renderConfig();
      case 'result':
        return this.renderResult();
      default:
        return this.renderNavigator();
    }
  }

  render() {
    return (
      <div className='license-navigator-v2-flow'>
        {this.props.showOverallProgress !== false ? this.renderProgress() : null}
        {this.props.voiceoverHandoff ? (
          <aside className='panel license-navigator__result margin-bottom20' role='note'>
            <p className='margin-bottom5'><strong>{this.props.voiceoverHandoff.title}</strong></p>
            <p className='margin-bottom5'>{this.props.voiceoverHandoff.summary}</p>
            <p className='text-size-small margin-bottom0'>{this.props.voiceoverHandoff.note}</p>
          </aside>
        ) : null}
        {this.renderCurrentStage()}
      </div>
    );
  }
}

LicenseNavigatorV2Flow.defaultProps = {
  locale: 'de',
  families: [],
  i18n: {
    back_to_navigator: '← Zurück zum Lizenz-Navigator',
    configure_recommendation: 'Lizenz für mein Projekt anpassen',
    ready_label: 'Danke für Ihre Angaben',
    ready_title: 'Wir empfehlen Ihnen die %{family}-Lizenz',
    ready_copy: 'Diese Lizenz passt nach Ihren Angaben am besten zu Ihrem Vorhaben. Prüfen Sie jetzt Preis, Lizenz-Upgrades und abgedeckte Nutzungsszenarien. Wenn alles passt, können Sie die Konfiguration direkt als Projekt-Lizenz speichern.',
    estimated_price: 'Preis für geplante Nutzung',
    selected_answers: 'Ihre Angaben im Detail',
    covered_scenarios: 'Abgedeckte Nutzungsszenarien',
    covered_scenarios_copy: 'Diese Nutzungsszenarien sind mit Ihrer aktuellen Konfiguration abgedeckt.',
    answers_toggle: 'Gegebene Antworten anzeigen',
    save_preset: 'Meine Projekt-Lizenz speichern',
    save_preset_done: 'Projekt-Lizenz gespeichert',
    save_overlay_title: 'Projekt-Lizenz speichern',
    save_overlay_copy: 'Geben Sie Ihrer Projekt-Lizenz einen Namen, damit Sie sie später schnell wiederverwenden können.',
    save_overlay_label: 'Name Ihrer Projekt-Lizenz',
    save_overlay_placeholder: 'z. B. Podcast-Projektlizenz',
    save_overlay_confirm: 'Projekt-Lizenz speichern',
    save_overlay_cancel: 'Abbrechen',
    copy_configuration_link: 'Link zur Lizenz kopieren',
    copy_configuration_link_done: 'Lizenz-Link kopiert',
    creating_configuration_link: 'Lizenz-Link wird erstellt',
    open_configuration_detail: 'Lizenz-Detailseite öffnen',
    close_save_overlay: 'Overlay schließen',
    save_requires_account: 'Projekt-Lizenz speichern',
    save_requires_account_copy: 'Zum Speichern dieser Projekt-Lizenz ist ein Kundenkonto erforderlich. Danach kann die Projekt-Lizenz später mit einem Klick auf Tracks angewendet werden.',
    sign_in_to_save: 'Einloggen und Projekt-Lizenz speichern',
    create_account_to_save: 'Registrieren und Projekt-Lizenz speichern',
    preset_saved: 'Projekt-Lizenz gespeichert',
    preset_saved_copy: 'Diese Projekt-Lizenz ist jetzt gespeichert und kann beim weiteren Musikeinkauf schnell wiederverwendet werden.',
    search_with_preset: 'Musik suchen und Projekt-Lizenz anwenden',
    create_another_preset: 'Weitere Projekt-Lizenz anlegen',
    edit_configuration: 'Konfiguration anpassen',
    restart: 'Neu starten',
    overall_progress: 'Gesamtfortschritt',
    finish_configuration: 'Konfiguration fertigstellen',
    recommended_license_family: 'Empfohlen für Ihr Projekt',
    config_intro_copy: 'Jetzt passen wir die Lizenz direkt auf Ihr Projekt an.'
  },
  customerSignedIn: false,
  signInPath: '/einloggen',
  signUpPath: '/registrieren',
  savePresetPath: null,
  signupContext: null,
  createConfigurationPath: null,
  searchPath: '/suche',
  skin: 'default',
  showOverallProgress: true,
  onOverallProgressChange: null,
  navigatorSidebarTitle: null,
  navigatorSidebarContextItems: [],
  navigatorSidebarActionLabel: '',
  onNavigatorSidebarAction: null,
  inlineConfigI18n: {
    heading: 'Lizenz-Quick-Config',
    subheading: '',
    customize: 'Lizenz für Ihr Projekt anpassen',
    reset: 'Zurücksetzen',
    selected_answers: 'Ausgewählte Bausteine',
    covered_scenarios: 'Abgedeckte Nutzungsszenarien',
    next_step: 'Nächster sinnvoller Schritt',
    final_scope: 'Aktueller Scope',
    base_price: 'Basispreis',
    estimated_price: 'Preis für geplante Nutzung',
    finished: 'Konfiguration abgeschlossen',
    start_hint: 'Die Basislizenz steht schon fest.',
    try_cart_copy: 'Ideal als Grundlage für eine spätere Projekt-Lizenz oder eine direkte Übernahme in einen Track-Kontext.',
    choose_option: 'Bitte wählen Sie eine Option.',
    more_questions: 'Weitere Frage',
    done: 'Fertig konfiguriert',
    back: 'Zurück',
    next: 'Weiter',
    result_step: 'Ergebnis',
    result_title: 'Ihre Lizenz-Konfiguration ist fertig',
    result_copy: 'Hier sehen Sie den aktuellen Scope mit Preis und abgedeckten Nutzungsszenarien.',
    edit_answers: 'Antworten ändern',
    result_action: 'Konfiguration fertigstellen',
    live_summary: 'Livestand',
    live_summary_title: 'Aktueller Stand für Ihr Projekt',
    live_summary_copy: 'Preis, ausgewählte Bausteine und die aktuell abgedeckten Nutzungsszenarien aktualisieren sich hier direkt mit jeder Antwort.'
  }
};

let elements = document.querySelectorAll('[data-react-component=LicenseNavigatorV2Flow]');

elements.forEach(function(element) {
  let reactProps = JSON.parse(element.dataset.reactProps);

  ReactDOM.render(
    <React.StrictMode>
      <LicenseNavigatorV2Flow {...reactProps} />
    </React.StrictMode>,
    element
  );
});

export default LicenseNavigatorV2Flow;
